import React from 'react';
import {
  Modal,
  Table,
  Typography,
  Space,
  Tag,
  Divider,
  Row,
  Col
} from 'antd';
import {
  FileTextOutlined,
  ClockCircleOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import { Invoice, InvoiceLineItem, InvoiceService } from '../services/invoiceService';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

interface InvoiceDetailsModalProps {
  open: boolean;
  onCancel: () => void;
  invoice: Invoice | null;
}

export const InvoiceDetailsModal: React.FC<InvoiceDetailsModalProps> = ({
  open,
  onCancel,
  invoice
}) => {
  if (!invoice) return null;

  const lineItemColumns: ColumnsType<InvoiceLineItem> = [
    {
      title: 'Type',
      key: 'type',
      width: 100,
      render: (_, record) => (
        <Tag color={record.metadata?.type === 'setup_fee' ? 'gold' : 'blue'}>
          {InvoiceService.getLineItemType(record)}
        </Tag>
      ),
    },
    {
      title: 'Description',
      key: 'description',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{InvoiceService.formatLineItemDescription(record)}</Text>
          {record.quantity && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Quantity: {record.quantity}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => {
        const period = InvoiceService.formatLineItemPeriod(record);
        return period ? (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {period}
          </Text>
        ) : (
          <Text type="secondary">One-time</Text>
        );
      },
    },
    {
      title: 'Amount',
      key: 'amount',
      align: 'right',
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
          <Text strong>
            {InvoiceService.formatCurrency(record.amount, record.currency)}
          </Text>
          {record.price?.unit_amount && record.quantity && record.quantity > 1 && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {InvoiceService.formatCurrency(record.price.unit_amount, record.currency)} each
            </Text>
          )}
        </Space>
      ),
    },
  ];

  const getTotalAmount = () => {
    if (!invoice.lines?.data) return invoice.amount_due;
    return invoice.lines.data.reduce((total, item) => total + item.amount, 0);
  };

  return (
    <Modal
      title={
        <Space>
          {invoice.is_pending_summary ? (
            <ClockCircleOutlined style={{ color: '#faad14' }} />
          ) : (
            <FileTextOutlined style={{ color: '#1890ff' }} />
          )}
          <span>
            {invoice.is_pending_summary ? 'Pending Charges Details' : 'Upcoming Invoice Details'}
          </span>
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Invoice Summary */}
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Space direction="vertical" size={0}>
              <Text type="secondary">Status</Text>
              <Tag color={InvoiceService.getUpcomingStatusColor(invoice.status)}>
                {InvoiceService.getUpcomingStatusText(invoice)}
              </Tag>
            </Space>
          </Col>
          <Col span={12}>
            <Space direction="vertical" size={0}>
              <Text type="secondary">Total Amount</Text>
              <Title level={4} style={{ margin: 0 }}>
                {InvoiceService.formatCurrency(invoice.amount_due, invoice.currency)}
              </Title>
            </Space>
          </Col>
        </Row>

        {invoice.is_pending_summary && (
          <div style={{ 
            padding: '12px 16px', 
            background: '#fff7e6', 
            border: '1px solid #ffd591',
            borderRadius: '6px'
          }}>
            <Space>
              <ClockCircleOutlined style={{ color: '#faad14' }} />
              <Text>
                These charges are pending and will be processed when your trial ends.
              </Text>
            </Space>
          </div>
        )}

        <Divider style={{ margin: '16px 0' }} />

        {/* Line Items */}
        <div>
          <Title level={5} style={{ marginBottom: 16 }}>
            <CreditCardOutlined /> Charge Breakdown
          </Title>
          
          {invoice.lines?.data && invoice.lines.data.length > 0 ? (
            <Table
              columns={lineItemColumns}
              dataSource={invoice.lines.data}
              rowKey="id"
              pagination={false}
              size="small"
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong>
                        {InvoiceService.formatCurrency(getTotalAmount(), invoice.currency)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '20px',
              background: '#fafafa',
              borderRadius: '6px'
            }}>
              <Text type="secondary">No detailed breakdown available</Text>
            </div>
          )}
        </div>

        {/* Additional Info */}
        {invoice.description && (
          <div>
            <Text type="secondary">Description: </Text>
            <Text>{invoice.description}</Text>
          </div>
        )}
      </Space>
    </Modal>
  );
}; 